import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import StatusBadge from '@/components/StatusBadge';
import {
  Loader2, Calendar, MapPin, X, Star, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatGBP, formatDate, shortId } from '@/lib/utils';

function ReviewForm({ bookingId, onSubmit, busy }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  return (
    <div className="mt-2 space-y-2 border-t border-slate-100 pt-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(n)}
            data-testid={`review-star-${n}`}
            className={`p-1 ${n <= rating ? 'text-amber-400' : 'text-slate-300'}`}
          >
            <Star className="h-5 w-5 fill-current" />
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-1">{rating}/5</span>
      </div>
      <Textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        placeholder="How was it?"
        data-testid={`review-comment-${bookingId}`}
      />
      <Button
        size="sm"
        disabled={busy}
        onClick={() => onSubmit(rating, comment)}
        data-testid={`review-submit-${bookingId}`}
        className="bg-rose-600 hover:bg-rose-700 text-white"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
        Post review
      </Button>
    </div>
  );
}

export default function CustomerBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reviewOpenId, setReviewOpenId] = useState(null);
  const [reviewedIds, setReviewedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customer/bookings');
      setBookings(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelBooking = useCallback(async (id) => {
    if (!window.confirm('Cancel this booking?')) return;
    setBusyId(id);
    try {
      const { data } = await api.patch(`/customer/bookings/${id}/cancel`);
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success('Booking cancelled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    } finally {
      setBusyId(null);
    }
  }, []);

  const submitReview = useCallback(async (bookingId, rating, comment) => {
    setBusyId(bookingId);
    try {
      await api.post('/customer/reviews', { booking_id: bookingId, rating, comment });
      toast.success('Review posted');
      setReviewedIds((prev) => new Set(prev).add(bookingId));
      setReviewOpenId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally {
      setBusyId(null);
    }
  }, []);

  const { upcoming, past } = useMemo(() => {
    const upcoming = [];
    const past = [];
    for (const b of bookings) {
      if (['completed', 'cancelled', 'refunded'].includes(b.status)) past.push(b);
      else upcoming.push(b);
    }
    return { upcoming, past };
  }, [bookings]);

  if (loading) return <p className="text-center text-sm text-slate-500 py-10"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading...</p>;

  const renderCard = (b) => {
    const isPast = ['completed', 'cancelled', 'refunded'].includes(b.status);
    const canCancel = !isPast;
    const canReview = b.status === 'completed' && !reviewedIds.has(b.id);
    return (
      <Card key={b.id} className="p-4 border-slate-200 space-y-2" data-testid={`customer-booking-${b.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{b.partner_name || 'Partner'}</p>
            <p className="text-xs text-slate-500">{b.service_name || 'Service'} · #{shortId(b.id)}</p>
          </div>
          <StatusBadge value={b.status} />
        </div>
        <div className="text-xs text-slate-600 space-y-0.5">
          {b.scheduled_at && <p className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(b.scheduled_at)}</p>}
          {b.customer_address && <p className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {b.customer_address}</p>}
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500">{formatGBP(b.total_amount)}</span>
          <StatusBadge value={b.payment_status} />
        </div>
        <div className="flex gap-2 pt-1">
          {canCancel && (
            <Button
              size="sm"
              variant="outline"
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={() => cancelBooking(b.id)}
              disabled={busyId === b.id}
              data-testid={`btn-cancel-${b.id}`}
            >
              {busyId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
              Cancel
            </Button>
          )}
          {canReview && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setReviewOpenId(reviewOpenId === b.id ? null : b.id)}
              data-testid={`btn-review-toggle-${b.id}`}
            >
              <Star className="h-3 w-3 mr-1" /> {reviewOpenId === b.id ? 'Close' : 'Leave review'}
            </Button>
          )}
        </div>
        {reviewOpenId === b.id && (
          <ReviewForm bookingId={b.id} busy={busyId === b.id} onSubmit={(r, c) => submitReview(b.id, r, c)} />
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-900">My bookings</h2>
      {bookings.length === 0 && (
        <Card className="p-6 text-center border-slate-200">
          <p className="text-sm text-slate-500">No bookings yet</p>
          <p className="text-xs text-slate-400 mt-1">Find pros near you on Home to make your first booking.</p>
        </Card>
      )}
      {upcoming.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 px-1 mb-2">Upcoming</p>
          <div className="space-y-3">{upcoming.map(renderCard)}</div>
        </div>
      )}
      {past.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 px-1 mb-2 mt-2">Past</p>
          <div className="space-y-3">{past.map(renderCard)}</div>
        </div>
      )}
    </div>
  );
}
